import React, { useEffect, useRef, useState } from 'react';

/**
 * Reveals its children the first time they scroll into view.
 *
 * Arming is decided during the first render, not in an effect, so the element
 * is never painted visible and then snatched back.
 *
 * Two deliberate safety valves, because this also wraps SEO content:
 *
 *  1. In Node (the build-time prerender) `window` is undefined, so nothing is
 *     ever armed and the markup stays plainly visible.
 *  2. After FALLBACK_MS, anything that is measurably inside the viewport but
 *     still hidden is revealed anyway. That covers an observer that failed to
 *     fire — including a crawler laying the page out in one very tall viewport
 *     — without pre-revealing content further down that the visitor has not
 *     scrolled to yet.
 *
 * Reveals once, then stops observing: this is an entrance, not a scrubber.
 *
 * @param {number} delay seconds to wait after entering view (for stagger)
 */
const FALLBACK_MS = 2500;

const Reveal = ({ children, className = '', delay = 0, ...rest }) => {
    const ref = useRef(null);

    const [armed] = useState(
        () =>
            typeof window !== 'undefined' &&
            typeof IntersectionObserver !== 'undefined' &&
            !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
    const [shown, setShown] = useState(false);

    useEffect(() => {
        if (!armed) return;
        const el = ref.current;
        if (!el) return;

        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShown(true);
                    io.disconnect();
                }
            },
            // Fire slightly before it is fully on screen, so the motion has
            // settled by the time the element is properly in view.
            { threshold: 0.05, rootMargin: '0px 0px -14% 0px' }
        );
        io.observe(el);

        // Failsafe — but only for something already ON SCREEN that the
        // observer somehow failed to report. A blanket timer revealed every
        // element on the page a couple of seconds after load, so anything
        // below the fold had already finished animating before you scrolled
        // to it, and the effect was invisible.
        const failsafe = setTimeout(() => {
            const rect = el.getBoundingClientRect();
            const inView = rect.top < window.innerHeight && rect.bottom > 0;
            if (inView) {
                setShown(true);
                io.disconnect();
            }
        }, FALLBACK_MS);

        return () => {
            io.disconnect();
            clearTimeout(failsafe);
        };
    }, [armed]);

    const classes = ['reveal-item', armed ? 'is-armed' : '', shown ? 'is-visible' : '', className]
        .filter(Boolean)
        .join(' ');

    return (
        <div ref={ref} className={classes} style={{ '--reveal-delay': `${delay}s` }} {...rest}>
            {children}
        </div>
    );
};

export default Reveal;
